import React, { useContext, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  FormFeedback,
  Spinner,
} from "reactstrap";
import PixwikLogo from "../../assets/pixwik/logo.png";
import bgImage from "../../assets/images/bg5.jpg";
import { useFormik } from "formik";
import * as Yup from "yup";

import AuthenticationApi from "Api/AuthenticationApi";
import UserContext from "ContextProvider/UserContext";
import { Toast } from 'primereact/toast';
import "./Login.scss";

const ForgetPasswordPage = () => {
  document.title = "Forget Password";

  const [pageState, setPageState] = useState("email");
  const [otpDialog, setOtpDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();
  const { } = useContext(UserContext);
  const toast = useRef(null);

  useEffect(() => {
    const themeMode = localStorage.getItem("theme-mode") || "light";
    setIsDarkMode(themeMode === "dark");
  }, []);

  const validation = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Please Enter Your Email"),
    }),
    onSubmit: (values) => {
      handleForgotPassword(values.email);
    },
  });

  const otpValidation = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string().required("Please Enter Your OTP"),
    }),
    onSubmit: (values) => {
      handleForgotOtpVerification(userEmail, values.otp);
    },
  });

  const resetPasswordValidation = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Please enter a new password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords do not match")
        .required("Please confirm your password"),
    }),
    onSubmit: (values) => {
      handleResetPassword(userEmail, values.newPassword);
    },
  });

  const handleForgotPassword = (email) => {
    setLoading(true);
    const payload = { email };

    AuthenticationApi.forgotPassword(payload)
      .then(() => {
        setLoading(false);
        setUserEmail(email); // Store the email for subsequent steps
        setOtpDialog(true);
        validation.resetForm();
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'OTP sent to email', life: 3000 });
      })
      .catch(() => {
        setLoading(false);
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Enter a valid email', life: 3000 });
      });
  };

  const handleForgotOtpVerification = (email, otp) => {
    setOtpLoading(true);
    const payload = { email, otp };

    AuthenticationApi.verifyForgotOtp(payload)
      .then((res) => {
        setOtpLoading(false);
        if (res.data.status) {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'OTP Verified Successfully', life: 3000 });
          otpValidation.resetForm();
          setPageState("reset");
        } else {
          toast.current.show({ severity: 'error', summary: 'Error', detail: res.data.message || "OTP verification failed", life: 3000 });
        }
      })
      .catch(() => {
        setOtpLoading(false);
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'OTP verification failed.', life: 3000 });
      });
  };

  const handleResetPassword = (email, newPassword) => {
    setResetLoading(true);
    const payload = { email, password: newPassword };

    AuthenticationApi.resetPassword(payload)
      .then(() => {
        setResetLoading(false);
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Password reset successfully', life: 3000 });
        navigate("/login");
      })
      .catch((err) => {
        setResetLoading(false);
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Password reset failed: ' + (err.response?.data?.message || ""), life: 3000 });
      });
  };

  return (
    <>
      <Toast ref={toast} position="top-right" life="3000" />
      <div
        className="account-pages login-page d-flex align-items-center justify-content-center"
        style={{
          backgroundImage: `url(${bgImage})`
        }}
      >
        <div className={`overlay ${isDarkMode ? 'dark-mode' : 'light-mode'}`} />
        <Container style={{ position: "relative", zIndex: 2 }}>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className={`overflow-hidden shadow-lg login-card ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                <Row className="g-0">
                  <Col md={12} className="d-flex justify-content-center align-items-center">
                    <CardBody className="p-4">
                      <div className="text-center mb-4">
                        <img
                          src={PixwikLogo}
                          alt="Pixwik Logo"
                          className="auth-logo"
                        />
                      </div>

                      {pageState === "reset" ? (
                        <div className="px-3">
                          <h4 className={`form-title ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                            Reset Password
                          </h4>

                          <form
                            className="form-horizontal mt-4"
                            onSubmit={(e) => {
                              e.preventDefault();
                              resetPasswordValidation.handleSubmit();
                            }}
                          >
                            <div className="mb-3">
                              <Label htmlFor="newPassword" className={`form-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                New Password
                              </Label>
                              <Input
                                name="newPassword"
                                className={`form-control ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                                placeholder="Enter new password"
                                type="password"
                                onChange={resetPasswordValidation.handleChange}
                                onBlur={resetPasswordValidation.handleBlur}
                                value={resetPasswordValidation.values.newPassword || ""}
                                invalid={
                                  resetPasswordValidation.touched.newPassword &&
                                  resetPasswordValidation.errors.newPassword
                                }
                              />
                              {resetPasswordValidation.touched.newPassword &&
                                resetPasswordValidation.errors.newPassword ? (
                                <FormFeedback type="invalid">
                                  {resetPasswordValidation.errors.newPassword}
                                </FormFeedback>
                              ) : null}
                            </div>

                            <div className="mb-3">
                              <Label htmlFor="confirmPassword" className={`form-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Confirm Password
                              </Label>
                              <Input
                                name="confirmPassword"
                                className={`form-control ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                                placeholder="Confirm password"
                                type="password"
                                onChange={resetPasswordValidation.handleChange}
                                onBlur={resetPasswordValidation.handleBlur}
                                value={resetPasswordValidation.values.confirmPassword || ""}
                                invalid={
                                  resetPasswordValidation.touched.confirmPassword &&
                                  resetPasswordValidation.errors.confirmPassword
                                }
                              />
                              {resetPasswordValidation.touched.confirmPassword &&
                                resetPasswordValidation.errors.confirmPassword ? (
                                <FormFeedback type="invalid">
                                  {resetPasswordValidation.errors.confirmPassword}
                                </FormFeedback>
                              ) : null}
                            </div>

                            <Row className="mb-3">
                              <div className="col-12 text-end">
                                <button
                                  className={`btn btn-primary ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                                  type="submit"
                                  disabled={resetLoading}
                                >
                                  {resetLoading ? <Spinner size="sm" /> : "Reset Password"}
                                </button>
                              </div>
                            </Row>
                          </form>
                        </div>
                      ) : otpDialog ? (
                        <div className="px-3">
                          <h4 className={`form-title ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                            OTP Verification
                          </h4>
                          <p className={`form-text ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                            Enter the OTP sent to your email {userEmail}
                          </p>

                          <form
                            className="form-horizontal mt-2"
                            onSubmit={(e) => {
                              e.preventDefault();
                              otpValidation.handleSubmit();
                            }}
                          >
                            <div className="mb-3">
                              <Label htmlFor="otp" className={`form-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                OTP
                              </Label>
                              <Input
                                name="otp"
                                type="text"
                                className={`form-control ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                                placeholder="Enter OTP"
                                onChange={otpValidation.handleChange}
                                onBlur={otpValidation.handleBlur}
                                value={otpValidation.values.otp || ""}
                                invalid={
                                  otpValidation.touched.otp && otpValidation.errors.otp
                                }
                              />
                              {otpValidation.touched.otp && otpValidation.errors.otp ? (
                                <FormFeedback type="invalid">
                                  {otpValidation.errors.otp}
                                </FormFeedback>
                              ) : null}
                            </div>

                            <Row className="mb-3">
                              <div className="col-6 back-button-otp user-select-none d-flex align-items-center">
                                <span
                                  className="d-flex align-items-center justify-content-center text-primary back-button"
                                  onClick={() => {
                                    setOtpDialog(false);
                                    otpValidation.resetForm();
                                  }}
                                >
                                  <i
                                    className="pi ion ion-ios-arrow-back"
                                    style={{ marginRight: "5px" }}
                                  />
                                  Back
                                </span>
                              </div>

                              <div className="col-6 text-end">
                                <button
                                  className={`btn btn-primary ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                                  type="submit"
                                  disabled={otpLoading}
                                >
                                  {otpLoading ? <Spinner size="sm" /> : "Verify OTP"}
                                </button>
                              </div>
                            </Row>
                          </form>
                        </div>
                      ) : (
                        <div className="px-3">
                          <h4 className={`form-title ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                            Reset Password
                          </h4>

                          <form
                            className="form-horizontal mt-4"
                            onSubmit={(e) => {
                              e.preventDefault();
                              validation.handleSubmit();
                            }}
                          >
                            <div className="mb-3">
                              <Label htmlFor="email" className={`form-label ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
                                Email
                              </Label>
                              <Input
                                name="email"
                                className={`form-control ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                                placeholder="Enter email"
                                type="email"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.email || ""}
                                invalid={
                                  validation.touched.email && validation.errors.email
                                }
                              />
                              {validation.touched.email && validation.errors.email ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.email}
                                </FormFeedback>
                              ) : null}
                            </div>

                            <Row className="mb-3">
                              <div className="col-12 text-end">
                                <button
                                  className={`btn btn-primary ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
                                  type="submit"
                                  disabled={loading}
                                >
                                  {loading ? <Spinner size="sm" /> : "Reset"}
                                </button>
                              </div>
                            </Row>
                          </form>
                        </div>
                      )}
                    </CardBody>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

ForgetPasswordPage.propTypes = {
  history: PropTypes.object,
};

export default ForgetPasswordPage;